import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Prompt, useHistory } from 'react-router-dom';
import { api } from '../services/api';
import { AuthContext } from '../contexts/AuthContext';
import { getOrigins } from '../utils/host';
import { isMicrosoftOAuthEnabled } from '../utils/env';
import { getStoredItem, removeStoredItem, setStoredItem } from '../utils/storage';
import CodeInput from './CodeInput';

const ENABLE_MICROSOFT_OAUTH = isMicrosoftOAuthEnabled();
const ORG_UPDATE_STATUS_KEY = 'finbase.organization_update_status';

const OrganizationDetails = ({ accountData, canManageAccount, onUpdated }) => {
  const stateOptions = [
    { code: '', name: 'Select state' },
    { code: 'AL', name: 'Alabama' },
    { code: 'AK', name: 'Alaska' },
    { code: 'AZ', name: 'Arizona' },
    { code: 'AR', name: 'Arkansas' },
    { code: 'CA', name: 'California' },
    { code: 'CO', name: 'Colorado' },
    { code: 'CT', name: 'Connecticut' },
    { code: 'DE', name: 'Delaware' },
    { code: 'DC', name: 'District of Columbia' },
    { code: 'FL', name: 'Florida' },
    { code: 'GA', name: 'Georgia' },
    { code: 'HI', name: 'Hawaii' },
    { code: 'ID', name: 'Idaho' },
    { code: 'IL', name: 'Illinois' },
    { code: 'IN', name: 'Indiana' },
    { code: 'IA', name: 'Iowa' },
    { code: 'KS', name: 'Kansas' },
    { code: 'KY', name: 'Kentucky' },
    { code: 'LA', name: 'Louisiana' },
    { code: 'ME', name: 'Maine' },
    { code: 'MD', name: 'Maryland' },
    { code: 'MA', name: 'Massachusetts' },
    { code: 'MI', name: 'Michigan' },
    { code: 'MN', name: 'Minnesota' },
    { code: 'MS', name: 'Mississippi' },
    { code: 'MO', name: 'Missouri' },
    { code: 'MT', name: 'Montana' },
    { code: 'NE', name: 'Nebraska' },
    { code: 'NV', name: 'Nevada' },
    { code: 'NH', name: 'New Hampshire' },
    { code: 'NJ', name: 'New Jersey' },
    { code: 'NM', name: 'New Mexico' },
    { code: 'NY', name: 'New York' },
    { code: 'NC', name: 'North Carolina' },
    { code: 'ND', name: 'North Dakota' },
    { code: 'OH', name: 'Ohio' },
    { code: 'OK', name: 'Oklahoma' },
    { code: 'OR', name: 'Oregon' },
    { code: 'PA', name: 'Pennsylvania' },
    { code: 'RI', name: 'Rhode Island' },
    { code: 'SC', name: 'South Carolina' },
    { code: 'SD', name: 'South Dakota' },
    { code: 'TN', name: 'Tennessee' },
    { code: 'TX', name: 'Texas' },
    { code: 'UT', name: 'Utah' },
    { code: 'VT', name: 'Vermont' },
    { code: 'VA', name: 'Virginia' },
    { code: 'WA', name: 'Washington' },
    { code: 'WV', name: 'West Virginia' },
    { code: 'WI', name: 'Wisconsin' },
    { code: 'WY', name: 'Wyoming' },
  ];
  const smsEnabled = Boolean(accountData.sms_enabled);
  const normalizedPolicy = accountData.mfa_channel_policy || 'any';
  const initialForm = {
    org_name: accountData.org_name || '',
    email: accountData.org_email || '',
    billing_legal_name: accountData.billing_legal_name || '',
    billing_addr_street: accountData.billing_addr_street || '',
    billing_addr_street_2: accountData.billing_addr_street_2 || '',
    billing_addr_city: accountData.billing_addr_city || '',
    billing_addr_state: accountData.billing_addr_state || '',
    billing_addr_zip: accountData.billing_addr_zip || '',
    mfa_required: Boolean(accountData.mfa_required),
    mfa_channel_policy: !smsEnabled && ['sms_only', 'sms_preferred'].includes(normalizedPolicy) ? 'email_only' : normalizedPolicy,
    mfa_require_oauth: Boolean(accountData.mfa_require_oauth),
    allowed_invite_domains: (accountData.allowed_invite_domains || []).join(', '),
  };
  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState({ type: '', message: '' });
  const [taxExemptionPostSaveWarning, setTaxExemptionPostSaveWarning] = useState('');
  const [saving, setSaving] = useState(false);
  const [showConvertConfirm, setShowConvertConfirm] = useState(false);
  const [converting, setConverting] = useState(false);
  const [authMethods, setAuthMethods] = useState([]);
  const [authMethodsInitial, setAuthMethodsInitial] = useState([]);
  const [availableProviders, setAvailableProviders] = useState([]);
  const [oidcProviders, setOidcProviders] = useState([]);
  const [oidcProvidersInitial, setOidcProvidersInitial] = useState([]);
  const [ssoCustomIdpEntitlement, setSsoCustomIdpEntitlement] = useState(accountData.sso_custom_idp_entitlement || { allowed: false });
  const [authLoading, setAuthLoading] = useState(false);
  const [showOidcModal, setShowOidcModal] = useState(false);
  const [oidcEditingId, setOidcEditingId] = useState(null);
  const [oidcStep, setOidcStep] = useState('create');
  const [oidcCallbackUrl, setOidcCallbackUrl] = useState('');
  const { idOrigin } = getOrigins();
  const [oidcLookupLoading, setOidcLookupLoading] = useState(false);
  const [oidcLookupError, setOidcLookupError] = useState('');
  const [oidcLookupStatus, setOidcLookupStatus] = useState('');
  const [oidcEndSessionError, setOidcEndSessionError] = useState('');
  const [oidcDeleteTarget, setOidcDeleteTarget] = useState(null);
  const [showOidcAdvanced, setShowOidcAdvanced] = useState(false);
  const [showOidcDiscoveryConfirm, setShowOidcDiscoveryConfirm] = useState(false);
  const [pendingOidcDiscoveryIssuer, setPendingOidcDiscoveryIssuer] = useState('');
  const [showOidcDeleteConfirm, setShowOidcDeleteConfirm] = useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [leaveVerificationCode, setLeaveVerificationCode] = useState('');
  const [leaveVerificationToken, setLeaveVerificationToken] = useState('');
  const [leaveVerificationStep, setLeaveVerificationStep] = useState('confirm');
  const [leaveVerificationStatus, setLeaveVerificationStatus] = useState('');
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [closeConfirmation, setCloseConfirmation] = useState('');
  const [closeVerificationCode, setCloseVerificationCode] = useState('');
  const [closeVerificationToken, setCloseVerificationToken] = useState('');
  const [closeVerificationStep, setCloseVerificationStep] = useState('confirm');
  const [closeVerificationStatus, setCloseVerificationStatus] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [showLifecycleComplete, setShowLifecycleComplete] = useState(false);
  const [lifecycleCompleteTitle, setLifecycleCompleteTitle] = useState('');
  const [lifecycleCompleteMessage, setLifecycleCompleteMessage] = useState('');
  const [lifecycleCompleteDetails, setLifecycleCompleteDetails] = useState([]);
  const [oidcForm, setOidcForm] = useState({
    name: '',
    issuer_url: '',
    auth_url: '',
    token_url: '',
    userinfo_url: '',
    jwks_url: '',
    end_session_url: '',
    client_id: '',
    client_secret: '',
    scope: 'openid email profile',
    enabled: true,
    force_login: false,
    pkce_enabled: true,
    token_endpoint_auth_method: 'client_secret_post',
    logout_with_idp: false,
    idp_logout_redirect_enabled: true,
  });
  const { logout } = useContext(AuthContext);
  const history = useHistory();
  const oidcPostLogoutRedirectUrl = `${idOrigin}/login?return_to=account&logged_out=1`;
  const canLeaveOrg = Boolean(accountData.can_leave_org);
  const canCloseOrg = Boolean(accountData.can_close_org);
  const closeConfirmationText = accountData.close_confirmation_text || (accountData.org_type === 'type-ind' ? 'CLOSE ACCOUNT' : accountData.org_name || '');
  const leaveBlockReason = accountData.leave_org_block_reason || '';
  const closeBlockReason = accountData.close_org_block_reason || '';
  const isOrgAccount = accountData.org_type === 'type-org';
  const lifecycleTitle = isOrgAccount ? 'Organization Access' : 'Account Access';
  const canUseSsoCustomIdp = Boolean(ssoCustomIdpEntitlement?.allowed);
  const ssoUpgradeMessage = 'Upgrade to Enterprise to enable SSO / Custom IdP Login.';
  const taxExemptStatus = accountData.tax_exempt_status || 'none';
  const historyRetentionText = useMemo(() => (
    isOrgAccount
      ? 'Closing this organization deactivates API keys, cancels pending invites, removes all members, and preserves historical records.'
      : 'Closing this account deactivates API keys, removes access, and preserves historical records.'
  ), [isOrgAccount]);
  const taxExemption = accountData.tax_exemption || null;
  useEffect(() => {
    setForm(initialForm);
  }, [accountData]);

  useEffect(() => {
    const stored = getStoredItem(ORG_UPDATE_STATUS_KEY);
    if (!stored) return;
    try {
      const parsed = JSON.parse(stored);
      if (parsed?.status) setStatus(parsed.status);
      if (parsed?.taxExemptionPostSaveWarning) setTaxExemptionPostSaveWarning(parsed.taxExemptionPostSaveWarning);
    } catch (err) {
      // ignore malformed storage payload
    } finally {
      removeStoredItem(ORG_UPDATE_STATUS_KEY);
    }
  }, [accountData.org_name]);

  const loadAuthOptions = async () => {
    setAuthLoading(true);
    try {
      const response = await api.get('/acct-mgmt/v1/account/org/auth-options');
      const methods = response.data?.methods || [];
      const providers = (response.data?.available_providers || []).filter((provider) => ENABLE_MICROSOFT_OAUTH || provider.code !== 'microsoft');
      const oidcList = response.data?.oidc_providers || [];
      setAuthMethods(methods);
      setAuthMethodsInitial(methods);
      setAvailableProviders(providers);
      setOidcProviders(oidcList);
      setOidcProvidersInitial(oidcList);
      setSsoCustomIdpEntitlement(response.data?.sso_custom_idp_entitlement || { allowed: false });
    } catch (err) {
      setStatus({ type: 'danger', message: err.response?.data?.error || 'Failed to load authentication policies.' });
    } finally {
      setAuthLoading(false);
    }
  };

  useEffect(() => {
    if (canManageAccount) {
      loadAuthOptions();
    }
  }, [canManageAccount]);

  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;
    setForm({ ...form, [name]: type === 'checkbox' ? checked : value });
  };

  const mfaPolicyNote = () => {
    if (!form.mfa_required) {
      return 'MFA is currently disabled for this organization.';
    }
    if (!smsEnabled && !['totp_only', 'totp_preferred'].includes(form.mfa_channel_policy)) {
      return 'Codes are sent by email.';
    }
    switch (form.mfa_channel_policy) {
      case 'totp_only':
        return 'Codes are generated by an authenticator app (TOTP).';
      case 'totp_preferred':
        return 'Codes use an authenticator app when enabled; otherwise email.';
      case 'sms_only':
        return 'Codes are sent by SMS (verified phone required).';
      case 'sms_preferred':
        return 'Codes are sent by SMS when a verified phone is on file; otherwise email.';
      case 'email_only':
        return 'Codes are sent by email.';
      default:
        return 'Codes use the user preference, defaulting to email.';
    }
  };

  const toggleAuthMethod = (providerCode) => {
    setAuthMethods((prev) => {
      const existing = prev.find((method) => method.provider_code === providerCode);
      if (existing) {
        return prev.map((method) =>
          method.provider_code === providerCode ? { ...method, enabled: !method.enabled } : method
        );
      }
      return [...prev, { provider_code: providerCode, enabled: true }];
    });
  };

  const toggleOidcProvider = (providerId) => {
    if (!canUseSsoCustomIdp) return;
    setOidcProviders((prev) =>
      prev.map((provider) =>
        provider.id === providerId ? { ...provider, enabled: !provider.enabled } : provider
      )
    );
  };

  const openOidcModal = (provider = null) => {
    if (!provider && !canUseSsoCustomIdp) return;
    if (provider) {
      setOidcStep('configure');
      setOidcEditingId(provider.id);
      setOidcCallbackUrl(provider.callback_url || `${idOrigin}/api/id/v1/auth/oidc/${provider.public_id}/callback`);
      setOidcForm({
        name: provider.name || '',
        issuer_url: provider.issuer_url || '',
        auth_url: provider.auth_url || '',
        token_url: provider.token_url || '',
        userinfo_url: provider.userinfo_url || '',
        jwks_url: provider.jwks_url || '',
        end_session_url: provider.end_session_url || '',
        client_id: provider.client_id || '',
        client_secret: provider.client_secret || '',
        scope: provider.scope || 'openid email profile',
        enabled: canUseSsoCustomIdp && provider.enabled !== false,
        force_login: Boolean(provider.force_login),
        pkce_enabled: provider.pkce_enabled !== false,
        token_endpoint_auth_method: provider.token_endpoint_auth_method || 'client_secret_post',
        logout_with_idp: provider.logout_with_idp !== false,
        idp_logout_redirect_enabled: provider.idp_logout_redirect_enabled !== false,
      });
    } else {
      setOidcStep('create');
      setOidcEditingId(null);
      setOidcCallbackUrl('');
      setOidcForm({
        name: '',
        issuer_url: '',
        auth_url: '',
        token_url: '',
        userinfo_url: '',
        jwks_url: '',
        end_session_url: '',
        client_id: '',
        client_secret: '',
        scope: 'openid email profile',
        enabled: canUseSsoCustomIdp,
        force_login: false,
        pkce_enabled: true,
        token_endpoint_auth_method: 'client_secret_post',
        logout_with_idp: false,
        idp_logout_redirect_enabled: true,
      });
    }
    setShowOidcAdvanced(false);
    setShowOidcModal(true);
  };

  const fetchOidcDiscovery = async (issuerUrl = oidcForm.issuer_url) => {
    if (!issuerUrl) {
      setOidcLookupError('Enter an issuer URL to fetch endpoints.');
      return;
    }
    setOidcLookupError('');
    setOidcLookupStatus('');
    setOidcLookupLoading(true);
    try {
      const response = await api.post('/acct-mgmt/v1/account/org/oidc/lookup', { issuer_url: issuerUrl });
      setOidcForm((prev) => ({
        ...prev,
        issuer_url: response.data?.issuer_url || prev.issuer_url,
        auth_url: response.data?.auth_url || prev.auth_url,
        token_url: response.data?.token_url || prev.token_url,
        userinfo_url: response.data?.userinfo_url || prev.userinfo_url,
        jwks_url: response.data?.jwks_url || prev.jwks_url,
        end_session_url: response.data?.end_session_url || prev.end_session_url,
      }));
      setOidcLookupStatus('success');
      setTimeout(() => setOidcLookupStatus(''), 3000);
    } catch (err) {
      setOidcLookupError(err.response?.data?.error || 'Failed to fetch OIDC endpoints.');
      setOidcLookupStatus('error');
      setTimeout(() => setOidcLookupStatus(''), 4000);
    } finally {
      setOidcLookupLoading(false);
    }
  };

  const oidcDiscoveryHasEndpointValues = () => Boolean(
    oidcForm.auth_url ||
    oidcForm.token_url ||
    oidcForm.userinfo_url ||
    oidcForm.jwks_url ||
    oidcForm.end_session_url
  );

  const requestOidcDiscoveryFetch = (issuerUrl = oidcForm.issuer_url) => {
    if (oidcDiscoveryHasEndpointValues()) {
      setPendingOidcDiscoveryIssuer(issuerUrl);
      setShowOidcDiscoveryConfirm(true);
      return;
    }
    fetchOidcDiscovery(issuerUrl);
  };

  const handleOidcIssuerBlur = (e) => {
    const issuerUrl = e.target.value?.trim();
    const discoveryFieldsEmpty = !oidcForm.auth_url &&
      !oidcForm.token_url &&
      !oidcForm.userinfo_url &&
      !oidcForm.jwks_url &&
      !oidcForm.end_session_url;
    if (issuerUrl && discoveryFieldsEmpty && !oidcLookupLoading) {
      fetchOidcDiscovery(issuerUrl);
    }
  };

  const hasOpenIdScope = (scope) => String(scope || '')
    .split(/\s+/)
    .map((value) => value.trim().toLowerCase())
    .includes('openid');

  const isOidcComplete = (provider) => Boolean(
    provider?.issuer_url &&
    provider?.auth_url &&
    provider?.token_url &&
    provider?.userinfo_url &&
    provider?.jwks_url &&
    provider?.client_id &&
    provider?.client_secret &&
    hasOpenIdScope(provider?.scope)
  );

  const oidcSetupChecks = (provider) => [
    { label: 'Provider name', complete: Boolean(provider?.name?.trim()), help: 'Name this IdP connection.' },
    { label: 'Issuer URL', complete: Boolean(provider?.issuer_url), help: 'Enter the issuer URL from your IdP.' },
    { label: 'Issuer configuration fetched', complete: Boolean(provider?.auth_url && provider?.token_url && provider?.userinfo_url && provider?.jwks_url), help: 'Fetch issuer configuration or use advanced overrides.' },
    { label: 'Client ID', complete: Boolean(provider?.client_id), help: 'Copy the client ID from your IdP app.' },
    { label: 'Client Secret', complete: Boolean(provider?.client_secret), help: 'Copy the client secret from your IdP app.' },
    { label: 'Scope includes openid', complete: hasOpenIdScope(provider?.scope), help: 'Scope must include openid.' },
    { label: 'Token auth method', complete: Boolean(provider?.token_endpoint_auth_method), help: 'Select the method configured on your IdP token endpoint.' },
    { label: 'Provider enabled', complete: Boolean(provider?.enabled && isOidcComplete(provider)), help: 'Enable after required setup is complete.' },
    { label: 'IdP logout configuration', complete: provider?.logout_with_idp === false || Boolean(provider?.end_session_url), help: 'Disable IdP logout or provide an end session URL.' },
  ];

  const saveOidcProvider = async () => {
    setStatus({ type: '', message: '' });
    setOidcLookupError('');
    setOidcEndSessionError('');
    if (oidcStep === 'configure' && oidcForm.logout_with_idp !== false && !String(oidcForm.end_session_url || '').trim()) {
      setOidcEndSessionError('End Session URL is required when IdP logout is enabled.');
      return;
    }
    try {
      const payload = canUseSsoCustomIdp ? oidcForm : { ...oidcForm, enabled: false };
      if (oidcEditingId) {
        await api.put(`/acct-mgmt/v1/account/org/oidc/${oidcEditingId}`, payload);
        setShowOidcModal(false);
        await loadAuthOptions();
        setStatus({ type: 'success', message: 'SSO / Custom IdP provider saved.' });
      } else {
        if (!canUseSsoCustomIdp) {
          setStatus({ type: 'warning', message: 'Upgrade to Enterprise to enable SSO / Custom IdP Login.' });
          return;
        }
        const response = await api.post('/acct-mgmt/v1/account/org/oidc', { name: oidcForm.name });
        const created = response.data?.provider;
        if (!created?.id) {
          throw new Error('OIDC provider was not created.');
        }
        setOidcEditingId(created.id);
        setOidcStep('configure');
        setOidcCallbackUrl(created.callback_url || `${idOrigin}/api/id/v1/auth/oidc/${created.public_id}/callback`);
        setOidcForm({
          name: created.name || oidcForm.name,
          issuer_url: created.issuer_url || '',
          auth_url: created.auth_url || '',
          token_url: created.token_url || '',
          userinfo_url: created.userinfo_url || '',
          jwks_url: created.jwks_url || '',
          end_session_url: created.end_session_url || '',
          client_id: created.client_id || '',
          client_secret: created.client_secret || '',
          scope: created.scope || 'openid email profile',
          enabled: created.enabled !== false,
          force_login: Boolean(created.force_login),
          pkce_enabled: created.pkce_enabled !== false,
          token_endpoint_auth_method: created.token_endpoint_auth_method || 'client_secret_post',
          logout_with_idp: created.logout_with_idp !== false,
          idp_logout_redirect_enabled: created.idp_logout_redirect_enabled !== false,
        });
        await loadAuthOptions();
        setStatus({ type: 'success', message: 'SSO / Custom IdP provider created. Add your IdP details to finish setup.' });
      }
    } catch (err) {
      setStatus({ type: 'danger', message: err.response?.data?.error || 'Failed to save OIDC provider.' });
    }
  };

  const deleteOidcProvider = async (providerId) => {
    setStatus({ type: '', message: '' });
    try {
      await api.delete(`/acct-mgmt/v1/account/org/oidc/${providerId}`);
      await loadAuthOptions();
      setStatus({ type: 'success', message: 'OIDC provider removed.' });
    } catch (err) {
      setStatus({ type: 'danger', message: err.response?.data?.error || 'Failed to delete OIDC provider.' });
    }
  };

  const normalizeAuthMethods = (methods) =>
    [...methods]
      .map((method) => ({ provider_code: method.provider_code, enabled: Boolean(method.enabled) }))
      .sort((a, b) => a.provider_code.localeCompare(b.provider_code));

  const normalizeOidcProviders = (providers) =>
    [...providers]
      .map((provider) => ({ id: provider.id, enabled: Boolean(provider.enabled) }))
      .sort((a, b) => (a.id || 0) - (b.id || 0));

  const formHasChanges = Object.keys(form).some((key) => {
    const currentValue = key === 'mfa_require_oauth' && !form.mfa_required ? false : form[key];
    return currentValue !== initialForm[key];
  });
  const authHasChanges = JSON.stringify(normalizeAuthMethods(authMethods)) !== JSON.stringify(normalizeAuthMethods(authMethodsInitial))
    || JSON.stringify(normalizeOidcProviders(oidcProviders)) !== JSON.stringify(normalizeOidcProviders(oidcProvidersInitial));
  const hasUnsavedChanges = formHasChanges || authHasChanges;

  useEffect(() => {
    if (!hasUnsavedChanges || saving) return undefined;
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges, saving]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ type: '', message: '' });
    setTaxExemptionPostSaveWarning('');
    setSaving(true);
    let resultStatus = null;

    const payload = {};
    if (!form.mfa_required && form.mfa_require_oauth) {
      setForm((prev) => ({ ...prev, mfa_require_oauth: false }));
    }
    Object.keys(form).forEach((key) => {
      let value = form[key];
      if (key === 'mfa_require_oauth' && !form.mfa_required) {
        value = false;
      }
      if (value !== initialForm[key]) {
        payload[key] = value;
      }
    });

    const authChanged = JSON.stringify(normalizeAuthMethods(authMethods)) !== JSON.stringify(normalizeAuthMethods(authMethodsInitial))
      || JSON.stringify(normalizeOidcProviders(oidcProviders)) !== JSON.stringify(normalizeOidcProviders(oidcProvidersInitial));

    if (authChanged) {
      const enabledMethods = authMethods.filter((method) => method.enabled);
      const enabledOidc = oidcProviders.filter((provider) => provider.enabled);
      if (enabledMethods.length + enabledOidc.length === 0) {
        setStatus({ type: 'danger', message: 'At least one login method must be enabled.' });
        setSaving(false);
        return;
      }
    }

    if (Object.keys(payload).length === 0 && !authChanged) {
      setStatus({ type: 'warning', message: 'No changes to save.' });
      setSaving(false);
      return;
    }

    try {
      const currentJurisdiction = String(taxExemption?.billing_jurisdiction || '').trim().toUpperCase();
      const nextJurisdiction = String(
        Object.prototype.hasOwnProperty.call(payload, 'billing_addr_state') ? (payload.billing_addr_state || '') : form.billing_addr_state || ''
      ).trim().toUpperCase();
      const hasAnyTrackedExemptionState = (
        String(taxExemption?.status || 'none') !== 'none'
        || (taxExemption?.approved_jurisdictions || []).length > 0
        || (taxExemption?.pending_review_jurisdictions || []).length > 0
      );
      const shouldWarnAfterSave = currentJurisdiction && nextJurisdiction && currentJurisdiction !== nextJurisdiction && hasAnyTrackedExemptionState;

      if (Object.keys(payload).length > 0) {
        if (payload.allowed_invite_domains !== undefined) {
          payload.allowed_invite_domains = payload.allowed_invite_domains
            .split(',')
            .map((entry) => entry.trim())
            .filter(Boolean);
        }
        const response = await api.put('/acct-mgmt/v1/account/org', payload);
        const stripeSync = response.data?.stripe_sync;
        if (stripeSync?.reason === 'sync_failed') {
          resultStatus = { type: 'warning', message: 'Organization details updated, but Stripe customer sync failed.' };
        } else if (stripeSync?.skipped === false) {
          resultStatus = { type: 'success', message: 'Organization details updated and billing profile synced.' };
        }
      }
      if (authChanged) {
        await api.put('/acct-mgmt/v1/account/org/auth-options', {
          methods: authMethods,
          oidc_providers: oidcProviders.map((provider) => ({ id: provider.id, enabled: provider.enabled })),
        });
        setAuthMethodsInitial(authMethods);
        setOidcProvidersInitial(oidcProviders);
      }
      const nextStatus = resultStatus || { type: 'success', message: 'Organization details updated.' };
      const nextTaxWarning = shouldWarnAfterSave
        ? 'Updating billing address can impact tax exemption status. Please verify tax exemption status on the Subscription page.'
        : '';
      setStatus(nextStatus);
      setTaxExemptionPostSaveWarning(nextTaxWarning);
      setStoredItem(ORG_UPDATE_STATUS_KEY, JSON.stringify({
        status: nextStatus,
        taxExemptionPostSaveWarning: nextTaxWarning,
      }));
      onUpdated();
    } catch (err) {
      setStatus({ type: 'danger', message: err.response?.data?.error || 'Failed to update organization.' });
    } finally {
      setSaving(false);
    }
  };

  const handleConvert = async () => {
    setStatus({ type: '', message: '' });
    setConverting(true);
    try {
      await api.post('/acct-mgmt/v1/account/org/convert');
      setStatus({ type: 'success', message: 'Account converted to organization.' });
      onUpdated();
    } catch (err) {
      setStatus({ type: 'danger', message: err.response?.data?.error || 'Failed to convert account.' });
    } finally {
      setConverting(false);
    }
  };

  const orgTypeLabel = accountData.org_type === 'type-org' ? 'Organization' : 'Individual';
  const oauthEnabled = authMethods.some((method) => method.enabled && !['password', 'otp_email'].includes(method.provider_code));
  const executeLifecycleAction = async (request, options = {}) => {
    setStatus({ type: '', message: '' });
    setActionLoading(true);
    try {
      const response = await request();
      if (response?.data?.access_revoked) {
        if (options.showCompletionBeforeLogout) {
          setLifecycleCompleteTitle(options.completionTitle || response?.data?.message || 'Action completed');
          setLifecycleCompleteMessage(response?.data?.message || 'Action completed.');
          setLifecycleCompleteDetails(Array.isArray(response?.data?.completion_details) ? response.data.completion_details : []);
          setShowLifecycleComplete(true);
        } else {
          logout();
          history.push('/login');
        }
        return;
      }
      setStatus({ type: 'success', message: response?.data?.message || 'Organization updated.' });
      onUpdated();
    } catch (err) {
      setStatus({ type: 'danger', message: err.response?.data?.error || 'Request failed.' });
    } finally {
      setActionLoading(false);
      setShowLeaveConfirm(false);
      setLeaveVerificationCode('');
      setLeaveVerificationToken('');
      setLeaveVerificationStep('confirm');
      setLeaveVerificationStatus('');
      setShowCloseConfirm(false);
      setCloseConfirmation('');
      setCloseVerificationCode('');
      setCloseVerificationToken('');
      setCloseVerificationStep('confirm');
      setCloseVerificationStatus('');
    }
  };

  return (
    <div>
      <Prompt
        when={hasUnsavedChanges && !saving}
        message="You have unsaved changes. Continue without saving?"
      />
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-3">
        <div>
          <h2 className="h4 fw-bold mb-1">Organization</h2>
          <p className="text-muted mb-0">Manage organization details and authentication policies.</p>
        </div>
        {canManageAccount && (
          <div className="d-flex align-items-center gap-2">
            {hasUnsavedChanges && <span className="text-warning small">Unsaved changes</span>}
            <button
              className="btn btn-primary"
              type="submit"
              form="organization-details-form"
              disabled={saving || !hasUnsavedChanges}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        )}
      </div>
      {status.message && (
        <div className={`alert alert-${status.type}`} role="alert">{status.message}</div>
      )}
      {canManageAccount ? (
        <form id="organization-details-form" onSubmit={handleSubmit}>
        <div className="card mb-3">
          <div className="card-body">
            <h5 className="card-title">Organization Details</h5>
            <div className="mb-2 text-muted">Account type: <strong>{orgTypeLabel}</strong></div>
            {accountData.org_type === 'type-ind' && (
              <div className="mb-3">
                <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => setShowConvertConfirm(true)}>
                  Convert Account to Organization Account Type
                </button>
                <div className="text-muted small mt-2">
                  Unlock team invites, user management, and more flexible authentication policies.
                </div>
              </div>
            )}
            <div className="mb-3">
              <label className="form-label">Organization Name</label>
              <input
                className="form-control"
                name="org_name"
                value={form.org_name}
                onChange={handleChange}
                required
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Organization Email</label>
              <input
                type="email"
                className="form-control"
                name="email"
                value={form.email}
                onChange={handleChange}
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Billing Legal Name</label>
              <div className="form-text mb-2">If different from Organization Name.</div>
              <input
                className="form-control mb-2"
                name="billing_legal_name"
                value={form.billing_legal_name}
                onChange={handleChange}
              />
              <label className="form-label">Billing Address</label>
              <div className="form-label small text-muted mb-1">Street Address</div>
              <input
                className="form-control mb-2"
                name="billing_addr_street"
                value={form.billing_addr_street}
                onChange={handleChange}
              />
              <label className="form-label small text-muted mb-1">Apt / Suite</label>
              <input
                className="form-control mb-2"
                name="billing_addr_street_2"
                value={form.billing_addr_street_2}
                onChange={handleChange}
              />
              <div className="row g-2">
                <div className="col-md-5">
                  <label className="form-label small text-muted mb-1">City</label>
                  <input
                    className="form-control"
                    name="billing_addr_city"
                    value={form.billing_addr_city}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-2">
                  <label className="form-label small text-muted mb-1">State</label>
                  <select
                    className="form-select"
                    name="billing_addr_state"
                    value={form.billing_addr_state}
                    onChange={handleChange}
                  >
                    {stateOptions.map((state) => (
                      <option key={state.code} value={state.code}>
                        {state.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-2">
                  <label className="form-label small text-muted mb-1">ZIP</label>
                  <input
                    className="form-control"
                    name="billing_addr_zip"
                    value={form.billing_addr_zip}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label small text-muted mb-1">Country</label>
                  <input
                    className="form-control"
                    value="United States (US)"
                    disabled
                    readOnly
                  />
                </div>
              </div>
            </div>
            <div className="mb-3">
              <label className="form-label">Tax Exemption</label>
              <div className="p-3 border rounded bg-light">
                <div className="fw-semibold">Status: <span className="text-capitalize">{String(taxExemptStatus).replace(/_/g, ' ')}</span></div>
                <div className="text-muted small mt-2">
                  Tax exemption requests are submitted and tracked from the{' '}
                  <button
                    type="button"
                    className="btn btn-link btn-sm p-0 align-baseline"
                    onClick={() => history.push('/account/subscription')}
                  >
                    Subscription screen
                  </button>.
                </div>
                {taxExemptionPostSaveWarning && (
                  <div className="alert alert-warning mt-3 mb-0 d-flex flex-wrap align-items-center justify-content-between gap-2" role="alert">
                    <span>{taxExemptionPostSaveWarning}</span>
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                      onClick={() => history.push('/account/subscription')}
                    >
                      Go to Subscription
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <h5 className="card-title">Authentication Policies</h5>
            <div className="mt-3 pt-2">
              <div className="fw-semibold mb-3">Allowed Sign-In Methods</div>
              {authLoading && <div className="text-muted">Loading authentication options...</div>}
              {!authLoading && (
                <div className="d-grid gap-2">
                  <div className="form-check form-switch finbase-toggle">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="auth-password"
                      checked={Boolean(authMethods.find((method) => method.provider_code === 'password')?.enabled)}
                      onChange={() => toggleAuthMethod('password')}
                    />
                    <label className="form-check-label" htmlFor="auth-password">
                      Email &amp; Password
                    </label>
                  </div>
                  <div className="form-check form-switch finbase-toggle">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="auth-otp-email"
                      checked={Boolean(authMethods.find((method) => method.provider_code === 'otp_email')?.enabled)}
                      onChange={() => toggleAuthMethod('otp_email')}
                    />
                    <label className="form-check-label" htmlFor="auth-otp-email">
                      Email One-Time Code
                    </label>
                  </div>
                  {availableProviders.map((provider) => (
                    <div key={provider.code} className="form-check form-switch finbase-toggle">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id={`auth-${provider.code}`}
                        checked={Boolean(authMethods.find((method) => method.provider_code === provider.code)?.enabled)}
                        onChange={() => toggleAuthMethod(provider.code)}
                        disabled={!provider.configured}
                      />
                      <label className="form-check-label" htmlFor={`auth-${provider.code}`}>
                        {provider.label} {!provider.configured && <span className="text-muted">(not configured)</span>}
                      </label>
                    </div>
                  ))}
                  {oidcProviders.length > 0 && (
                    <div className="d-grid gap-2">
                      <div className="small fw-semibold text-muted mt-1">Custom SSO / IdP Providers</div>
                      {oidcProviders.map((provider) => {
                        const providerComplete = provider.is_complete ?? isOidcComplete(provider);
                        const callbackUrl = provider.callback_url || `${idOrigin}/api/id/v1/auth/oidc/${provider.public_id}/callback`;
                        return (
                          <React.Fragment key={provider.id}>
                            <div className="d-flex justify-content-between align-items-center gap-3 rounded border px-3 py-2">
                              <div className="form-check form-switch finbase-toggle m-0">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id={`oidc-enabled-${provider.id}`}
                                  checked={provider.enabled !== false}
                                  onChange={() => toggleOidcProvider(provider.id)}
                                  disabled={!providerComplete || !canUseSsoCustomIdp}
                                />
                                <label className="form-check-label" htmlFor={`oidc-enabled-${provider.id}`}>
                                  {provider.name} <span className="text-muted fw-normal">(SSO / Custom IdP)</span>
                                </label>
                              </div>
                              <div className="d-flex align-items-center gap-2">
                                {!providerComplete && (
                                  <span className="text-muted small">Complete setup to enable</span>
                                )}
                                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => openOidcModal(provider)}>
                                  Edit
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => {
                                    setOidcDeleteTarget(provider);
                                    setShowOidcDeleteConfirm(true);
                                  }}
                                >
                                  Remove
                                </button>
                              </div>
                            </div>
                          </React.Fragment>
                        );
                      })}
                    </div>
                  )}
                  {!canUseSsoCustomIdp && oidcProviders.length > 0 && (
                    <div className="small px-3 py-2 mt-3 rounded border-start border-3 border-info bg-light">
                      <div className="fw-semibold mb-1">SSO / Custom IdP requires Enterprise</div>
                      <div>{ssoUpgradeMessage}</div>
                      <div className="text-muted mt-1">Your saved provider settings will remain available and can be re-enabled after upgrading.</div>
                    </div>
                  )}
                </div>
              )}
              <div className="mt-3">
                {canUseSsoCustomIdp ? (
                  <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => openOidcModal()}>
                    Add SSO / Custom IdP
                  </button>
                ) : oidcProviders.length === 0 ? (
                  <div className="small px-3 py-2 rounded border-start border-3 border-info bg-light">
                    <div className="fw-semibold mb-1">SSO / Custom IdP requires Enterprise</div>
                    <div>{ssoUpgradeMessage}</div>
                  </div>
                ) : (
                  <button type="button" className="btn btn-sm btn-outline-primary" disabled>
                    Add SSO / Custom IdP
                  </button>
                )}
              </div>
            </div>

            <div className="mt-4 pt-3 border-top">
              <div className="d-flex justify-content-between align-items-center">
                <div className="fw-semibold">Multi-Factor Authentication</div>
                {form.mfa_required ? (
                  <div className="text-success d-flex align-items-center gap-2 small">
                    <i className="fas fa-check-circle" aria-hidden="true"></i>
                    MFA Enabled for All Users in Organization
                  </div>
                ) : (
                  <div className="text-muted d-flex align-items-center gap-2 small">
                    <i className="fas fa-info-circle" aria-hidden="true"></i>
                    MFA is currently off. We recommend enabling it for stronger account protection.
                  </div>
                )}
              </div>
              <div className="form-check form-switch finbase-toggle mt-3">
                <div className="d-flex align-items-start gap-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="mfa-required"
                    name="mfa_required"
                    checked={form.mfa_required}
                    onChange={handleChange}
                  />
                  <div>
                    <label className="form-check-label" htmlFor="mfa-required">
                      Require MFA for Email &amp; Password Sign-Ins
                    </label>
                    <div className="text-muted small mt-1">{mfaPolicyNote()}</div>
                  </div>
                </div>
              </div>
              <div className="form-check form-switch finbase-toggle mt-3">
                <div className="d-flex align-items-start gap-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="mfa-require-oauth"
                    name="mfa_require_oauth"
                    checked={form.mfa_require_oauth}
                    onChange={handleChange}
                    disabled={!form.mfa_required || !oauthEnabled}
                  />
                  <div>
                    <label className="form-check-label" htmlFor="mfa-require-oauth">
                      Require MFA for OAuth Sign-Ins
                    </label>
                    <div className="text-muted small mt-1">Does not apply to custom OIDC.</div>
                    {!oauthEnabled && (
                      <div className="text-muted small mt-1">Enable an OAuth provider to use this option.</div>
                    )}
                    {!form.mfa_required && (
                      <div className="text-muted small mt-1">Enable MFA for email &amp; password to use this option.</div>
                    )}
                  </div>
                </div>
              </div>
              <div className="mt-3">
                <label className="form-label">MFA Channel Policy</label>
                <select
                  className="form-select"
                  name="mfa_channel_policy"
                  value={form.mfa_channel_policy}
                  onChange={handleChange}
                  disabled={!form.mfa_required}
                >
                  <option value="any">Any (User Preference)</option>
                  <option value="totp_preferred">Authenticator App Preferred</option>
                  <option value="totp_only">Authenticator App Only</option>
                  {smsEnabled && <option value="sms_preferred">SMS Preferred</option>}
                  {smsEnabled && <option value="sms_only">SMS Only</option>}
                  <option value="email_only">Email Only</option>
                </select>
                {smsEnabled && ['sms_only', 'sms_preferred', 'any'].includes(form.mfa_channel_policy) && (
                  <div className="form-text text-muted">
                    By enabling SMS verification, you consent to receive security codes by text message. Message and data rates may apply.
                  </div>
                )}
              </div>
            </div>
            {accountData.org_type === 'type-org' && (
              <div className="mt-4 pt-3 border-top">
                <div className="fw-semibold mb-2">Invite Email Domain Restrictions (Optional)</div>
                <div className="text-muted small mb-2">
                  Only allow invites to these domains. Leave blank to allow any domain.
                </div>
                <input
                  className="form-control"
                  name="allowed_invite_domains"
                  value={form.allowed_invite_domains}
                  onChange={handleChange}
                  placeholder="example.com, example2.com"
                />
              </div>
            )}
          </div>
        </div>
        </form>
      ) : (
        <div className="card mb-3">
          <div className="card-body">
            <h5 className="card-title">Organization Details</h5>
            <p className="text-muted mb-0">You do not have permission to edit organization details or authentication policies.</p>
          </div>
        </div>
      )}
      <div className="card mt-3">
        <div className="card-body">
          <h5 className="card-title">{lifecycleTitle}</h5>
          <p className="text-muted mb-3">{historyRetentionText}</p>
          {isOrgAccount ? (
            <div className="d-flex flex-wrap gap-2 align-items-center">
              <button
                type="button"
                className="btn btn-outline-danger"
                onClick={() => setShowLeaveConfirm(true)}
                disabled={!canLeaveOrg || actionLoading}
                title={!canLeaveOrg ? leaveBlockReason : undefined}
              >
                Leave Organization
              </button>
              {canManageAccount && (
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => setShowCloseConfirm(true)}
                  disabled={!canCloseOrg || actionLoading}
                  title={!canCloseOrg ? closeBlockReason : undefined}
                >
                  Close Organization
                </button>
              )}
            </div>
          ) : (
            <div className="d-flex flex-wrap gap-2 align-items-center">
              <button
                type="button"
                className="btn btn-danger"
                onClick={() => setShowCloseConfirm(true)}
                disabled={!canCloseOrg || actionLoading}
                title={!canCloseOrg ? closeBlockReason : undefined}
              >
                Close Account
              </button>
            </div>
          )}
          {!canLeaveOrg && leaveBlockReason && isOrgAccount && (
            <div className="text-muted small mt-2">{leaveBlockReason}</div>
          )}
          {!canCloseOrg && closeBlockReason && (
            <div className="text-muted small mt-2">{closeBlockReason}</div>
          )}
        </div>
      </div>
      {showLeaveConfirm && (
        <>
          <div className="modal-backdrop show"></div>
          <div className="modal d-block" tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Leave organization?</h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={() => setShowLeaveConfirm(false)}
                    disabled={actionLoading}
                  ></button>
                </div>
                <div className="modal-body">
                  {leaveVerificationStep === 'confirm' ? (
                    <>
                      <p className="mb-2">You will lose access immediately.</p>
                      <p className="mb-0 text-muted">
                        We will send a verification code to your email. You must confirm that code before you can leave this organization.
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="mb-2 text-center">Enter the verification code sent to your email to confirm leaving this organization.</p>
                      <div className="d-flex justify-content-center mb-2">
                        <CodeInput value={leaveVerificationCode} onChange={setLeaveVerificationCode} disabled={actionLoading} autoFocus />
                      </div>
                      {leaveVerificationStatus && <div className="text-muted small text-center">{leaveVerificationStatus}</div>}
                    </>
                  )}
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => {
                      setShowLeaveConfirm(false);
                      setLeaveVerificationCode('');
                      setLeaveVerificationToken('');
                      setLeaveVerificationStep('confirm');
                      setLeaveVerificationStatus('');
                    }}
                    disabled={actionLoading}
                  >
                    Cancel
                  </button>
                  {leaveVerificationStep === 'confirm' ? (
                    <button
                      type="button"
                      className="btn btn-danger"
                      disabled={actionLoading}
                      onClick={async () => {
                        setActionLoading(true);
                        setLeaveVerificationStatus('');
                        try {
                          const response = await api.post('/acct-mgmt/v1/account/org/leave/request');
                          setLeaveVerificationToken(response.data?.verification_token || '');
                          setLeaveVerificationStep('verify');
                          setLeaveVerificationStatus('Verification code sent.');
                        } catch (err) {
                          setStatus({ type: 'danger', message: err.response?.data?.error || 'Failed to send verification code.' });
                        } finally {
                          setActionLoading(false);
                        }
                      }}
                    >
                      {actionLoading ? 'Sending Code...' : 'Send Verification Code'}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-danger"
                      disabled={actionLoading || leaveVerificationCode.length < 6 || !leaveVerificationToken}
                      onClick={async () => {
                        setActionLoading(true);
                        try {
                          const verifyResponse = await api.post('/acct-mgmt/v1/account/org/leave/verify', {
                            code: leaveVerificationCode,
                            verification_token: leaveVerificationToken,
                          });
                          await executeLifecycleAction(
                            () => api.post('/acct-mgmt/v1/account/org/leave', {
                              verification_token: verifyResponse.data?.verification_token,
                            }),
                            {
                              showCompletionBeforeLogout: true,
                              completionTitle: 'You Have Left the Organization',
                            }
                          );
                        } catch (err) {
                          setLeaveVerificationCode('');
                          setLeaveVerificationStatus(err.response?.data?.error || 'Verification failed.');
                          setActionLoading(false);
                        }
                      }}
                    >
                      {actionLoading ? 'Leaving...' : 'Verify and Leave'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
      {showCloseConfirm && (
        <>
          <div className="modal-backdrop show"></div>
          <div className="modal d-block" tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{isOrgAccount ? 'Close organization?' : 'Close account?'}</h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={() => {
                      setShowCloseConfirm(false);
                      setCloseConfirmation('');
                      setCloseVerificationCode('');
                      setCloseVerificationToken('');
                      setCloseVerificationStep('confirm');
                      setCloseVerificationStatus('');
                    }}
                    disabled={actionLoading}
                  ></button>
                </div>
                <div className="modal-body">
                  {closeVerificationStep === 'confirm' ? (
                    <>
                      <p className="mb-2">
                        This action immediately removes access, deactivates API keys, cancels pending invites, and preserves historical records.
                      </p>
                      <p className="mb-2 text-muted">
                        Any active subscription will not renew. Closing the organization now forfeits the remainder of the current subscription term, and fees already paid are not prorated.
                      </p>
                      <p className="mb-3 text-muted">
                        Type <strong>{closeConfirmationText}</strong> to confirm.
                      </p>
                      <input
                        className="form-control"
                        value={closeConfirmation}
                        onChange={(e) => setCloseConfirmation(e.target.value)}
                        placeholder={closeConfirmationText}
                        autoFocus
                      />
                    </>
                  ) : (
                    <>
                      <p className="mb-2 text-center">
                        Enter the verification code sent to your email to confirm {isOrgAccount ? 'closing this organization' : 'closing this account'}.
                      </p>
                      <div className="d-flex justify-content-center mb-2">
                        <CodeInput value={closeVerificationCode} onChange={setCloseVerificationCode} disabled={actionLoading} autoFocus />
                      </div>
                      {closeVerificationStatus && <div className="text-muted small text-center">{closeVerificationStatus}</div>}
                    </>
                  )}
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => {
                      setShowCloseConfirm(false);
                      setCloseConfirmation('');
                      setCloseVerificationCode('');
                      setCloseVerificationToken('');
                      setCloseVerificationStep('confirm');
                      setCloseVerificationStatus('');
                    }}
                    disabled={actionLoading}
                  >
                    Cancel
                  </button>
                  {closeVerificationStep === 'confirm' ? (
                    <button
                      type="button"
                      className="btn btn-danger"
                      disabled={actionLoading || closeConfirmation.trim() !== closeConfirmationText}
                      onClick={async () => {
                        setActionLoading(true);
                        setCloseVerificationStatus('');
                        try {
                          const response = await api.post('/acct-mgmt/v1/account/org/close/request');
                          setCloseVerificationToken(response.data?.verification_token || '');
                          setCloseVerificationStep('verify');
                          setCloseVerificationStatus('Verification code sent.');
                        } catch (err) {
                          setStatus({ type: 'danger', message: err.response?.data?.error || 'Failed to send verification code.' });
                        } finally {
                          setActionLoading(false);
                        }
                      }}
                    >
                      {actionLoading ? 'Sending Code...' : 'Send Verification Code'}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-danger"
                      disabled={actionLoading || closeVerificationCode.length < 6 || !closeVerificationToken}
                      onClick={async () => {
                        setActionLoading(true);
                        try {
                          const verifyResponse = await api.post('/acct-mgmt/v1/account/org/close/verify', {
                            code: closeVerificationCode,
                            verification_token: closeVerificationToken,
                          });
                          await executeLifecycleAction(
                            () => api.post('/acct-mgmt/v1/account/org/close', {
                              confirmation: closeConfirmation.trim(),
                              verification_token: verifyResponse.data?.verification_token,
                            }),
                            {
                              showCompletionBeforeLogout: true,
                              completionTitle: isOrgAccount ? 'Organization Closed' : 'Account Closed',
                            }
                          );
                        } catch (err) {
                          setCloseVerificationCode('');
                          setCloseVerificationStatus(err.response?.data?.error || 'Verification failed.');
                          setActionLoading(false);
                        }
                      }}
                    >
                      {actionLoading ? 'Closing...' : (isOrgAccount ? 'Verify and Close Organization' : 'Verify and Close Account')}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
      {showLifecycleComplete && (
        <>
          <div className="modal-backdrop show"></div>
          <div className="modal d-block" tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{lifecycleCompleteTitle}</h5>
                </div>
                <div className="modal-body">
                  <p className="mb-3">{lifecycleCompleteMessage}</p>
                  {lifecycleCompleteDetails.length > 0 && (
                    <ul className="mb-0">
                      {lifecycleCompleteDetails.map((detail) => (
                        <li key={detail}>{detail}</li>
                      ))}
                    </ul>
                  )}
                  {!lifecycleCompleteDetails.length && (
                    <p className="mb-0 text-muted">
                      A confirmation email has been sent. You can sign in again later to create your own organization or join another one with this same email address.
                    </p>
                  )}
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => {
                      setShowLifecycleComplete(false);
                      logout();
                      history.push('/login');
                    }}
                  >
                    Continue
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
      {showConvertConfirm && (
        <>
          <div className="modal-backdrop show"></div>
          <div className="modal d-block" tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Convert to organization?</h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={() => setShowConvertConfirm(false)}
                    disabled={converting}
                  ></button>
                </div>
                <div className="modal-body">
                  <p className="mb-0">
                    This is a one-way change. You’ll be able to add users and manage invitations.
                  </p>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setShowConvertConfirm(false)}
                    disabled={converting}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={converting}
                    onClick={async () => {
                      await handleConvert();
                      setShowConvertConfirm(false);
                    }}
                  >
                    {converting ? 'Converting...' : 'Yes, Convert'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
      {showOidcModal && (
        <>
          <div className="modal-backdrop show"></div>
          <div className="modal d-block" tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-xl modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    {oidcEditingId ? 'Configure SSO / Custom IdP' : 'Add SSO / Custom IdP'}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={() => setShowOidcModal(false)}
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="row g-4">
                    <div className="col-lg-8">
                      <div className="mb-2">
                        <label className="form-label">Provider Name</label>
                        <input
                          className="form-control"
                          value={oidcForm.name}
                          onChange={(e) => setOidcForm({ ...oidcForm, name: e.target.value })}
                          placeholder="Acme SSO"
                        />
                      </div>
                      {oidcStep === 'create' && (
                        <div className="text-muted small">
                          Create the provider first to generate the callback URL you’ll need in your IdP.
                        </div>
                      )}
                      {oidcStep === 'configure' && (
                        <>
                      <div className="mb-3">
                        <label className="form-label">Callback URL</label>
                        <div className="d-flex gap-2">
                          <input className="form-control" value={oidcCallbackUrl} readOnly />
                          <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={() => navigator.clipboard?.writeText(oidcCallbackUrl || '')}
                            disabled={!oidcCallbackUrl}
                          >
                            Copy
                          </button>
                        </div>
                        <div className="small text-muted mt-1">
                          Add this URL to your IdP app before completing the configuration below.
                        </div>
                      </div>
                      <div className="mb-2">
                        <label className="form-label">Issuer URL</label>
                        <input
                          className="form-control"
                          value={oidcForm.issuer_url}
                          onChange={(e) => setOidcForm({ ...oidcForm, issuer_url: e.target.value })}
                          onBlur={handleOidcIssuerBlur}
                          placeholder="https://idp.example.com"
                        />
                        <div className="mt-2 d-flex flex-wrap align-items-center gap-2">
                          <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={() => requestOidcDiscoveryFetch()}
                            disabled={oidcLookupLoading || !oidcForm.issuer_url}
                          >
                            {oidcLookupLoading ? 'Fetching...' : 'Fetch remaining URLs from issuer'}
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={() => setShowOidcAdvanced((prev) => !prev)}
                          >
                            {showOidcAdvanced ? 'Hide advanced overrides' : 'Advanced endpoint overrides'}
                          </button>
                          {oidcLookupStatus === 'success' && (
                            <span className="text-success small">Configuration loaded from issuer discovery.</span>
                          )}
                          {oidcLookupStatus === 'error' && (
                            <span className="text-danger small">Discovery failed. Check the issuer URL or open advanced overrides.</span>
                          )}
                        </div>
                        {oidcLookupError && (
                          <div className="text-danger small mt-1">{oidcLookupError}</div>
                        )}
                        {showOidcAdvanced && (
                          <div className="border rounded p-3 mt-3 bg-light">
                            <div className="alert alert-warning small mb-3">
                              Only override these if your IdP requires custom endpoints. URLs must be public HTTPS endpoints and cannot point to private, local, or reserved addresses.
                            </div>
                            <div className="mb-2">
                              <label className="form-label">Authorization URL</label>
                              <input
                                className="form-control"
                                value={oidcForm.auth_url}
                                onChange={(e) => setOidcForm({ ...oidcForm, auth_url: e.target.value })}
                                placeholder="https://idp.example.com/oauth2/v2.0/authorize"
                              />
                            </div>
                            <div className="mb-2">
                              <label className="form-label">Token URL</label>
                              <input
                                className="form-control"
                                value={oidcForm.token_url}
                                onChange={(e) => setOidcForm({ ...oidcForm, token_url: e.target.value })}
                                placeholder="https://idp.example.com/oauth2/v2.0/token"
                              />
                            </div>
                            <div className="mb-2">
                              <label className="form-label">User Info URL</label>
                              <input
                                className="form-control"
                                value={oidcForm.userinfo_url}
                                onChange={(e) => setOidcForm({ ...oidcForm, userinfo_url: e.target.value })}
                                placeholder="https://idp.example.com/oauth2/v2.0/userinfo"
                              />
                            </div>
                            <div className="mb-0">
                              <label className="form-label">JWKS URL</label>
                              <input
                                className="form-control"
                                value={oidcForm.jwks_url}
                                onChange={(e) => setOidcForm({ ...oidcForm, jwks_url: e.target.value })}
                                placeholder="https://idp.example.com/.well-known/jwks.json"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="mb-2">
                        <label className="form-label">Client ID</label>
                        <input
                          className="form-control"
                          value={oidcForm.client_id}
                          onChange={(e) => setOidcForm({ ...oidcForm, client_id: e.target.value })}
                        />
                      </div>
                      <div className="mb-2">
                        <label className="form-label">Client Secret</label>
                        <input
                          type="password"
                          className="form-control"
                          value={oidcForm.client_secret}
                          onChange={(e) => setOidcForm({ ...oidcForm, client_secret: e.target.value })}
                        />
                      </div>
                      <div className="mb-2">
                        <label className="form-label">Token Endpoint Auth Method</label>
                        <select
                          className="form-select"
                          value={oidcForm.token_endpoint_auth_method || 'client_secret_post'}
                          onChange={(e) => setOidcForm({ ...oidcForm, token_endpoint_auth_method: e.target.value })}
                        >
                          <option value="client_secret_post">Client secret POST</option>
                          <option value="client_secret_basic">Client secret Basic</option>
                        </select>
                        <div className="text-muted small mt-1">
                          Match the client authentication method configured on your identity provider token endpoint.
                        </div>
                      </div>
                      <div className="mb-2">
                        <label className="form-label">Scope</label>
                        <input
                          className="form-control"
                          value={oidcForm.scope}
                          onChange={(e) => setOidcForm({ ...oidcForm, scope: e.target.value })}
                          placeholder="openid email profile"
                        />
                      </div>
                      <div className="alert alert-info small mb-3">
                        Finbase validates the ID token signature against the IdP signing keys and requires <code>openid</code> scope plus an email-compatible claim in <code>email</code>, <code>preferred_username</code>, or <code>upn</code>. The returned email must match the invited or existing Finbase user email.
                      </div>
                      <div className="form-check form-switch finbase-toggle">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="oidc-enabled"
                          checked={oidcForm.enabled}
                          onChange={(e) => setOidcForm({ ...oidcForm, enabled: e.target.checked })}
                          disabled={!isOidcComplete(oidcForm) || !canUseSsoCustomIdp}
                        />
                        <label className="form-check-label" htmlFor="oidc-enabled">
                          Enabled {!isOidcComplete(oidcForm) && <span className="text-muted">(complete setup to enable)</span>}
                        </label>
                      </div>
                      {!canUseSsoCustomIdp && (
                        <div className="text-muted small mt-2">Upgrade to Enterprise to enable SSO / Custom IdP Login.</div>
                      )}
                      <div className="form-check form-switch finbase-toggle mt-2">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="oidc-force-login"
                          checked={oidcForm.force_login}
                          onChange={(e) => setOidcForm({ ...oidcForm, force_login: e.target.checked })}
                        />
                        <label className="form-check-label" htmlFor="oidc-force-login">
                          Force re-authentication at login
                        </label>
                      </div>
                      <div className="form-check form-switch finbase-toggle mt-2">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="oidc-pkce-enabled"
                          checked={oidcForm.pkce_enabled !== false}
                          onChange={(e) => setOidcForm({ ...oidcForm, pkce_enabled: e.target.checked })}
                        />
                        <label className="form-check-label" htmlFor="oidc-pkce-enabled">
                          Use PKCE
                        </label>
                      </div>
                      {oidcForm.pkce_enabled === false && (
                        <div className="text-warning small mt-1">
                          Recommended. Disable only if your identity provider does not support PKCE.
                        </div>
                      )}
                      <div className="form-check form-switch finbase-toggle mt-2">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="oidc-logout-with-idp"
                          checked={oidcForm.logout_with_idp !== false}
                          onChange={(e) => setOidcForm({ ...oidcForm, logout_with_idp: e.target.checked })}
                        />
                        <label className="form-check-label" htmlFor="oidc-logout-with-idp">
                          Log out of IdP when logging out of Finbase
                        </label>
                      </div>
                      {oidcForm.logout_with_idp !== false && (
                        <div className="border rounded p-3 mt-3">
                          <div className="fw-semibold mb-2">Logout Behavior</div>
                          <div>
                            <label className="form-label">End Session URL</label>
                            <input
                              className={`form-control ${oidcEndSessionError ? 'is-invalid' : ''}`}
                              value={oidcForm.end_session_url}
                              onChange={(e) => {
                                setOidcForm({ ...oidcForm, end_session_url: e.target.value });
                                if (oidcEndSessionError) setOidcEndSessionError('');
                              }}
                              placeholder="https://idp.example.com/oauth2/v2.0/logout"
                            />
                            {oidcEndSessionError && (
                              <div className="invalid-feedback d-block">{oidcEndSessionError}</div>
                            )}
                            {!oidcEndSessionError && !oidcForm.end_session_url && (
                              <div className="form-text">
                                Use Fetch remaining URLs from issuer above to populate this automatically when supported by your IdP.
                              </div>
                            )}
                          </div>
                          <div className="form-check form-switch finbase-toggle mt-3">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id="oidc-logout-redirect-enabled"
                              checked={oidcForm.idp_logout_redirect_enabled !== false}
                              onChange={(e) => setOidcForm({ ...oidcForm, idp_logout_redirect_enabled: e.target.checked })}
                            />
                            <label className="form-check-label" htmlFor="oidc-logout-redirect-enabled">
                              Redirect back to Finbase after IdP logout
                            </label>
                          </div>
                          {oidcForm.idp_logout_redirect_enabled !== false && (
                            <div className="mt-2">
                              <label className="form-label small text-muted">Post-Logout Redirect URI</label>
                              <div className="d-flex gap-2">
                                <input className="form-control" value={oidcPostLogoutRedirectUrl} readOnly />
                                <button
                                  type="button"
                                  className="btn btn-outline-secondary"
                                  onClick={() => navigator.clipboard?.writeText(oidcPostLogoutRedirectUrl || '')}
                                  disabled={!oidcPostLogoutRedirectUrl}
                                >
                                  Copy
                                </button>
                              </div>
                              <div className="form-text">
                                Allow this URI in your IdP logout redirect settings. Disable redirect if your IdP does not support post-logout redirects.
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                        </>
                      )}
                    </div>
                    {oidcStep === 'configure' && (
                      <div className="col-lg-4">
                        <div className="border rounded p-3 bg-light position-sticky" style={{ top: '1rem' }}>
                          <div className="fw-semibold mb-2">Setup Status</div>
                          <div className="d-flex flex-column gap-2">
                            {oidcSetupChecks(oidcForm).map((check) => (
                              <div key={check.label} className="d-flex gap-2 align-items-start">
                                <span className={`badge rounded-pill ${check.complete ? 'bg-success' : 'bg-secondary'}`}>
                                  {check.complete ? '✓' : '•'}
                                </span>
                                <div>
                                  <div className={check.complete ? 'fw-semibold text-success' : 'fw-semibold'}>
                                    {check.label}
                                  </div>
                                  {!check.complete && (
                                    <div className="small text-muted">{check.help}</div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-outline-secondary" onClick={() => setShowOidcModal(false)}>
                    Cancel
                  </button>
                  {oidcStep === 'create' ? (
                    <button type="button" className="btn btn-primary" onClick={saveOidcProvider} disabled={!oidcForm.name.trim() || !canUseSsoCustomIdp}>
                      Create Provider
                    </button>
                  ) : (
                    <button type="button" className="btn btn-primary" onClick={saveOidcProvider}>
                      Save Provider
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
      {showOidcDiscoveryConfirm && (
        <>
          <div className="modal-backdrop show"></div>
          <div className="modal d-block" tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Replace issuer configuration?</h5>
                  <button type="button" className="btn-close" aria-label="Close" onClick={() => setShowOidcDiscoveryConfirm(false)}></button>
                </div>
                <div className="modal-body">
                  Fetching issuer configuration will replace existing endpoint URLs with values from the issuer discovery document.
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-outline-secondary" onClick={() => setShowOidcDiscoveryConfirm(false)}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => {
                      const issuerUrl = pendingOidcDiscoveryIssuer || oidcForm.issuer_url;
                      setShowOidcDiscoveryConfirm(false);
                      setPendingOidcDiscoveryIssuer('');
                      fetchOidcDiscovery(issuerUrl);
                    }}
                  >
                    Fetch & Replace
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
      {showOidcDeleteConfirm && (
        <>
          <div className="modal-backdrop show"></div>
          <div className="modal d-block" tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Remove OIDC provider?</h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={() => setShowOidcDeleteConfirm(false)}
                  ></button>
                </div>
                <div className="modal-body">
                  <p className="mb-0">
                    This will remove <strong>{oidcDeleteTarget?.name || 'this provider'}</strong> from your allowed sign-in methods.
                  </p>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setShowOidcDeleteConfirm(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={async () => {
                      if (oidcDeleteTarget?.id) {
                        await deleteOidcProvider(oidcDeleteTarget.id);
                      }
                      setShowOidcDeleteConfirm(false);
                      setOidcDeleteTarget(null);
                    }}
                  >
                    Remove
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default OrganizationDetails;
